import { makeArticle } from "./learnFactory";
import type { LearnFAQ } from "./learnShared";
import type { RelatedLink as RL } from "@/components/RelatedPages";

/**
 * /learn/ampa-tms-florida — PROMPT 7
 * Hub for "ampa tms florida" / "ampa tms miami" (currently all directories;
 * Google AI Overview already cites us). Drive times verified with Google
 * Maps. Do not name other clinics.
 */

const AMPA_TRADEMARK =
  "Ampa and Ampa One are trademarks of Ampa Inc. Neuro Wellness TMS Centers of America is an independent provider and is not owned by Ampa.";

const faqs: LearnFAQ[] = [
  {
    question: "Where can I get Ampa TMS in Miami?",
    answer:
      "Neuro Wellness TMS Centers of America offers FDA-cleared Ampa One TMS in Coral Springs, about a 45-minute drive from Miami. Accelerated schedules mean fewer trips, so patients from across South Florida can complete treatment in a few days. Call 954-827-9793 to schedule.",
  },
  {
    question: "Where can I get Ampa TMS in Fort Lauderdale?",
    answer:
      "Our Coral Springs clinic is about a 25-minute drive from Fort Lauderdale. We offer FDA-cleared Ampa One TMS with accelerated schedules. Call 954-827-9793 to schedule a free consultation.",
  },
  {
    question: "Where can I get Ampa TMS in Boca Raton?",
    answer:
      "Our Coral Springs clinic is about a 25-minute drive from Boca Raton. We offer FDA-cleared Ampa One TMS with accelerated schedules. Call 954-827-9793 to schedule a free consultation.",
  },
  {
    question: "Where can I get Ampa TMS in West Palm Beach?",
    answer:
      "Our Coral Springs clinic is about a 50-minute drive from West Palm Beach. We offer FDA-cleared Ampa One TMS with accelerated schedules, so the drive is manageable for a short course. Call 954-827-9793 to schedule.",
  },
  {
    question: "Is there Ampa One TMS near me in Florida?",
    answer:
      "Neuro Wellness TMS Centers of America in Coral Springs offers FDA-cleared Ampa One TMS, centrally located for Miami-Dade, Broward, and Palm Beach patients. To confirm whether a clinic uses Ampa One, check Ampa's official provider locator at ampahealth.com/find-a-provider. Accelerated schedules mean patients from across Florida and out of state can complete treatment in a few days.",
  },
];

export const pageAmpaTmsFlorida = makeArticle({
  slug: "ampa-tms-florida",
  title:
    "Ampa One TMS in Florida: Miami, Fort Lauderdale, Boca Raton & Palm Beach",
  description:
    "Where to get FDA-cleared Ampa One TMS in Florida. Drive times from Coral Springs to Miami, Fort Lauderdale, Boca Raton, and Palm Beach, plus travel options and how to verify a provider.",
  canonical: "/learn/ampa-tms-florida",
  h1: "Where to Get Ampa One TMS in Florida",
  breadcrumbLabel: "Ampa One TMS in Florida",
  shortAnswer:
    "Neuro Wellness TMS Centers of America offers FDA-cleared Ampa One TMS in Coral Springs, centrally located for Miami-Dade, Broward, and Palm Beach patients. Accelerated schedules mean fewer trips, so patients from across Florida and out of state can complete treatment in a few days. Individual results vary. No outcome is guaranteed. Coverage varies by plan. Ampa One is available by prescription after a clinical evaluation.",
  keyTakeaways: [
    "Ampa One TMS is offered in Coral Springs, central to Miami-Dade, Broward, and Palm Beach.",
    "Drive times: Fort Lauderdale ~25 min, Boca Raton ~25 min, Miami ~45 min, West Palm Beach ~50 min.",
    "Accelerated schedules mean fewer trips — patients from across Florida and out of state can finish in a few days.",
    "Verify a clinic uses Ampa One at ampahealth.com/find-a-provider (we are listed).",
    "Weekend availability; call 954-827-9793 to confirm current hours.",
  ],
  sections: [
    {
      heading: "Quick answer",
      paragraphs: [
        "Neuro Wellness TMS Centers of America offers FDA-cleared Ampa One TMS in Coral Springs, centrally located for Miami-Dade, Broward, and Palm Beach patients. Accelerated schedules mean fewer trips, so patients from across Florida and out of state can complete treatment in a few days instead of commuting for 4–6 weeks.",
      ],
    },
    {
      heading: "Drive times from Coral Springs",
      paragraphs: [
        "Our clinic is at 9734 West Sample Road, 2nd Floor, Suite B, Coral Springs, FL 33065. Approximate drive times (verify with Google Maps before you travel):",
      ],
      table: {
        headers: ["From", "Drive time to Coral Springs"],
        rows: [
          ["Fort Lauderdale", "~25 min"],
          ["Boca Raton", "~25 min"],
          ["Miami", "~45 min"],
          ["West Palm Beach", "~50 min"],
          ["Naples", "~1 hr 45 min"],
          ["Tampa / Orlando", "~3 hr"],
        ],
      },
    },
    {
      heading: "Coming from farther away",
      paragraphs: [
        "Patients from outside South Florida can complete an accelerated Ampa One course in a few days rather than commuting for weeks. Nearby hotels, a travel companion, and follow-up at home make a short stay practical. See [[traveling for treatment|/learn/travel-for-saint-tms]] (the same logistics apply to Ampa One).",
      ],
    },
    {
      heading: "How to check whether a clinic uses Ampa One",
      paragraphs: [
        "Ampa's official provider locator at ampahealth.com/find-a-provider shows which clinics are listed — and we are listed. We do not name other clinics. If a clinic claims to offer Ampa One, confirm it on the locator and ask whether a psychiatrist supervises your care.",
      ],
    },
    {
      heading: "Our location, hours, and weekend availability",
      paragraphs: [
        "Neuro Wellness TMS Centers of America is at 9734 West Sample Road, 2nd Floor, Suite B, Coral Springs, FL 33065. Call 954-827-9793 to confirm our current hours and weekend availability.",
      ],
    },
  ],
  faqs,
  sources: [
    {
      citation:
        "Ampa Inc. Find a provider locator. ampahealth.com/find-a-provider (accessed November 3, 2026).",
      url: "https://www.ampahealth.com/find-a-provider",
    },
    {
      citation: "Google Maps. Drive time estimates, Coral Springs, FL.",
    },
  ],
  relatedLinks: [
    {
      title: "Ampa One™ TMS in Florida",
      href: "/ampa",
      description: "Camera-guided, accelerated depression treatment",
    },
    {
      title: "One-Day TMS in Florida",
      href: "/one-day-tms",
      description: "The Ampa One Day protocol explained",
    },
    {
      title: "Ampa One TMS Cost",
      href: "/learn/ampa-tms-cost",
      description: "Price, insurance, and payment options",
    },
    {
      title: "SAINT® vs Ampa One",
      href: "/learn/saint-vs-ampa-one",
      description: "Two accelerated TMS options, one Florida clinic",
    },
    {
      title: "Schedule a Consultation",
      href: "/contactus",
      description: "Book a free consultation with our clinical team",
    },
  ] as RL[],
  reviewBlock: {
    writtenBy: "Neuro Wellness TMS Centers of America",
    reviewedBy: "Daniel Pietras, MD",
    reviewedByHref: "/dr-daniel-pietras-md",
    published: "November 3, 2026",
    lastReviewed: "November 3, 2026",
    lastUpdated: "November 3, 2026",
  },
  toc: [
    { label: "Quick answer", href: "#quick-answer" },
    { label: "Drive times", href: "#drive-times-from-coral-springs" },
    { label: "Coming from farther away", href: "#coming-from-farther-away" },
    {
      label: "Verify a provider",
      href: "#how-to-check-whether-a-clinic-uses-ampa-one",
    },
    {
      label: "Location & hours",
      href: "#our-location-hours-and-weekend-availability",
    },
    { label: "FAQ", href: "#faq" },
    { label: "Cited research", href: "#cited-research" },
  ],
  safetyBlock:
    "Individual results vary. No outcome is guaranteed. Coverage varies by plan. Ampa One is available by prescription after a clinical evaluation. Common side effects include mild scalp discomfort and headache that usually fade. If you are in crisis, call or text 988.",
  trademarkFooter: AMPA_TRADEMARK,
  ctaBand: {
    headline: "Get Ampa One TMS in Florida",
    primaryText: "Request a free consultation",
    primaryHref: "/ampa",
    secondaryText: "Call 954-827-9793",
    secondaryHref: "tel:9548279793",
    phone: "954-827-9793",
  },
});
